/**
 * Pagina di errore applicativo — agganciata al router come `errorComponent`.
 *
 * Prima qualsiasi eccezione di render finiva sulla 404 ("Pagina non trovata"): messaggio
 * fuorviante, perché la rotta esiste ed è il contenuto che è andato in errore. Qui si dice
 * cosa è successo, si offre un ritenta/ricarica e — in sviluppo — lo stack, che è ciò che
 * serve davvero per capire il problema.
 */
import { Link } from '@tanstack/react-router';
import {
  Button, Card, Center, Container, Group, Stack, Text, ThemeIcon, Title,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowLeft, IconRefresh } from '@tabler/icons-react';
import { mw } from '@/shared/mobile/tokens';
import { MachineText } from '@/shared/components/Codice';

interface Props {
  error: Error;
  /** `reset` del router: ritenta il render senza ricaricare la pagina. */
  reset?: () => void;
}

export function AppErrorPage({ error, reset }: Props) {
  const message = error?.message?.trim() || 'Errore imprevisto durante il caricamento della pagina.';
  return (
    <Container size="sm" py={48}>
      <Card withBorder radius="lg" padding="xl" shadow="sm">
        <Stack gap={14} align="center">
          <ThemeIcon size={56} radius="xl" variant="light" color="red"><IconAlertTriangle size={28} /></ThemeIcon>
          <Title order={2} ta="center" style={{ fontSize: 'var(--fs-h3)', color: mw.ink900 }}>Qualcosa è andato storto</Title>
          <Text ta="center" fz="sm" c="dimmed" maw={460}>
            La pagina esiste, ma il suo contenuto non è riuscito a caricarsi. Puoi ritentare: se
            l&apos;errore si ripete, segnalalo con il messaggio qui sotto.
          </Text>
          <MachineText fz="xs" style={{ width: '100%', maxHeight: 160, overflow: 'auto' }}>
            {message}
            {import.meta.env.DEV && error?.stack ? `\n\n${error.stack}` : ''}
          </MachineText>
          <Group gap={10} mt={4}>
            {reset && (
              <Button leftSection={<IconRefresh size={16} />} onClick={reset}>Ritenta</Button>
            )}
            <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={() => window.location.reload()}>
              Ricarica
            </Button>
            <Button component={Link} to="/" variant="subtle" leftSection={<IconArrowLeft size={16} />}>
              Torna alla home
            </Button>
          </Group>
        </Stack>
      </Card>
      <Center mt={12}>
        <Text fz="xs" c="dimmed">Se il problema riguarda i dati, spesso basta ricaricare: la cache viene rigenerata.</Text>
      </Center>
    </Container>
  );
}
